import { useState, useEffect, useMemo } from "react";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import { numeroPerExcel, scaricaExcel, FMT_EURO } from "../exportExcel";
import "../Ricavi/Ricavi.css";
import "../Clienti/Clienti.css"; // riuso classi admin-clienti-docs-* per la tabella
import "../../TransazioniRavvicinate.css"; // classi globali .pagination-compact, .items-btn-compact, ...
import "./DataQuality.css";
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

// Colonne tabella nell'ordine di rendering. `type` indica come confrontarle nel sort.
const COLUMNS = [
    { label: "N. Documento",      key: "numero_documento",      type: "string" },
    { label: "Codice Conto",      key: "codice_conto",          type: "string" },
    { label: "Descrizione Conto", key: "des_conto",             type: "string" },
    { label: "Partner",           key: "des_partner",           type: "string" },
    { label: "Move Type",         key: "move_type",             type: "string" },
    { label: "Sezione",           key: "sezione",               type: "string" },
    { label: "Data Fattura",      key: "data_fattura",          type: "date"   },
    { label: "Data Competenza",   key: "data_competenza",       type: "date"   },
    { label: "Comp. Start",       key: "competence_start_date", type: "date"   },
    { label: "Comp. End",         key: "competence_end_date",   type: "date"   },
    { label: "Importo",           key: "importo_leggibile",     type: "number" },
    { label: "Sede",              key: "des_sede",              type: "string" },
    { label: "Id Settore",        key: "id_settore",            type: "string" },
    { label: "Id Commessa",       key: "id_commessa",           type: "string" },
    { label: "Targa Veicolo",     key: "targa_veicolo",         type: "string" },
    { label: "Plan 5",            key: "des_plan5",             type: "string" },
    { label: "Plan 6",            key: "des_plan6",             type: "string" },
    { label: "Plan 7",            key: "des_plan7",             type: "string" },
    { label: "Plan 10",           key: "des_plan10",            type: "string" },
    { label: "Plan 11",           key: "des_plan11",            type: "string" },
];

// Comparatore per il sort. NULL/undefined sempre in fondo a prescindere dalla direzione.
const compareValues = (a, b, type) => {
    const aNil = a == null || a === "";
    const bNil = b == null || b === "";
    if (aNil && bNil) return 0;
    if (aNil) return 1;
    if (bNil) return -1;
    if (type === "number") return Number(a) - Number(b);
    // date in formato ISO (YYYY-MM-DD...): il confronto lessicografico equivale a quello cronologico.
    if (type === "date")   return String(a).localeCompare(String(b));
    return String(a).localeCompare(String(b), "it", { numeric: true, sensitivity: "base" });
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

const fmtDateText = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString("it-IT");
};

const fmtEuro = (v) => {
    if (v == null) return "";
    const n = Number(v);
    if (!isFinite(n)) return "";
    return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

const DataQuality = ({ filters }) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Paginazione lato client (il dataset arriva già completo dall'endpoint)
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    // Ordinamento lato client (pattern allineato a TransazioniRavvicinate)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    // ESC per uscire dal fullscreen
    useEffect(() => {
        const onEsc = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, []);

    // Quando cambia il dataset, l'ordinamento o il pageSize torno a pagina 1
    useEffect(() => { setCurrentPage(1); }, [rows, pageSize, sortConfig]);

    // Fetch dataset al cambio dei filtri applicati (gestiti dal pannello globale Admin.js)
    useEffect(() => {
        if (!filters) return;
        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token");
                const params = {};
                if (filters.dqNumeroDocumento) params.numero_documento = filters.dqNumeroDocumento;
                const codiceContoCsv = (filters.dqCodiceConto || []).map(s => s.value).join(",");
                if (codiceContoCsv) params.codice_conto = codiceContoCsv;
                const desContoCsv = (filters.dqDesConto || []).map(s => s.value).join(",");
                if (desContoCsv) params.des_conto = desContoCsv;
                const desPartnerCsv = (filters.dqDesPartner || []).map(s => s.value).join(",");
                if (desPartnerCsv) params.des_partner = desPartnerCsv;
                const moveTypeCsv = (filters.dqMoveType || []).map(s => s.value).join(",");
                if (moveTypeCsv) params.move_type = moveTypeCsv;
                const desSedeCsv = (filters.dqDesSede || []).map(s => s.value).join(",");
                if (desSedeCsv) params.des_sede = desSedeCsv;
                if (filters.dqDataFatturaFrom) params.data_fattura_from = filters.dqDataFatturaFrom;
                if (filters.dqDataFatturaTo)   params.data_fattura_to   = filters.dqDataFatturaTo;

                const { data } = await axios.get(`${API_URL}/admin/data-quality/dataset`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setRows(Array.isArray(data?.rows) ? data.rows : []);
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                console.error("Errore fetch Data Quality:", err);
                if (!cancelled) setError("Errore caricamento dati");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; controller.abort(); };
    }, [filters]);

    // ── Export Excel ──
    // Tabella piatta: al controller condiviso bastano { headers, rows }.
    const handleExport = async () => {
        const colonne = [
            "numero_documento", "codice_conto", "des_conto", "des_partner", "move_type", "sezione",
            "data_fattura", "data_competenza", "competence_start_date", "competence_end_date", "importo_leggibile",
            "des_sede", "id_settore", "id_commessa", "targa_veicolo",
            "des_plan5", "des_plan6", "des_plan7", "des_plan10", "des_plan11",
        ];
        // Solo importo_leggibile va convertito in numero: sugli altri campi (codici
        // conto, targhe) la conversione mangerebbe eventuali zeri iniziali.
        const righe = rows.map(r => colonne.map(c => (
            c === "importo_leggibile" ? numeroPerExcel(r[c]) : (r[c] ?? "")
        )));
        // Il backend mette in grassetto l'ultima riga: ci mettiamo il totale, così
        // il grassetto cade su una riga che lo merita e non sull'ultimo documento.
        righe.push(colonne.map(c => {
            if (c === "numero_documento") return "Totale";
            if (c === "importo_leggibile") return numeroPerExcel(totaleImporto);
            return "";
        }));

        const numFmt = colonne.map(c => c === "importo_leggibile" ? FMT_EURO : "");

        try {
            await scaricaExcel({
                endpoint: "admin/data-quality/downloadExcel",
                tableName: "Data Quality",
                datiDaStampare: { headers: colonne, rows: righe, formattazione: { numFmt, negativiRossi: true, freeze: true } },
                filename: `data_quality_${new Date().toISOString().slice(0, 10)}.xlsx`,
            });
        } catch (err) {
            console.error("Errore durante il download del file:", err);
            setError("Errore durante l'esportazione del file Excel");
        }
    };

    const totaleImporto = useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.importo_leggibile) || 0), 0),
        [rows]
    );

    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(currentPage, totalPages);

    // Ordino tutto il dataset filtrato, poi paginazione sulle righe ordinate.
    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;
        const col = COLUMNS.find(c => c.key === sortConfig.key);
        const type = col?.type || "string";
        const dirMul = sortConfig.direction === "desc" ? -1 : 1;
        const copy = [...rows];
        copy.sort((a, b) => dirMul * compareValues(a[sortConfig.key], b[sortConfig.key], type));
        return copy;
    }, [rows, sortConfig]);

    const paginatedRows = useMemo(
        () => sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize),
        [sortedRows, safePage, pageSize]
    );

    return (
        <div className="admin-ricavi-container admin-data-quality">
            <div className={`admin-ricavi-table-section${isFullscreen ? " fullscreen-trend" : ""}`}>
                <RotateDevice active={isFullscreen} />
                <div className="admin-ricavi-table-header">
                    <h3 className="admin-ricavi-section-title">Data Quality — righe CE con settore o commessa mancante</h3>
                    <div className="admin-ricavi-actions">
                        <button className="admin-ricavi-download-btn" onClick={handleExport} title="Esporta in Excel" disabled={rows.length === 0}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta
                        </button>
                        <button
                            className="trend-fullscreen-btn"
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                        >
                            <FullscreenIcon active={isFullscreen} />
                        </button>
                    </div>
                </div>
                <div className="admin-ricavi-table-wrapper admin-clienti-docs-wrapper">
                    {loading ? (
                        <Lf23Loader />
                    ) : (
                        <table className="admin-ricavi-table admin-clienti-docs-table">
                            <thead>
                                <tr>
                                    {COLUMNS.map(({ label, key }) => (
                                        <th
                                            key={key}
                                            className="transazioni-th-sortable"
                                            onClick={() => handleSort(key)}
                                        >
                                            {label}
                                            <span className="transazioni-sort-icon">
                                                {sortConfig.key === key
                                                    ? (sortConfig.direction === "asc" ? " ▲" : " ▼")
                                                    : " ⇅"}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {error ? (
                                    <tr>
                                        <td colSpan={20} style={{ textAlign: "center", padding: "32px", color: "#c00" }}>{error}</td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={20} style={{ textAlign: "center", padding: "32px", color: "#888" }}>
                                            Nessun dato disponibile
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {paginatedRows.map((r, idx) => (
                                            <tr key={idx}>
                                                <td>{r.numero_documento ?? ""}</td>
                                                <td>{r.codice_conto ?? ""}</td>
                                                <td className="admin-clienti-td-desc">{r.des_conto ?? ""}</td>
                                                <td className="admin-clienti-td-desc">{r.des_partner ?? ""}</td>
                                                <td>{r.move_type ?? ""}</td>
                                                <td>{r.sezione ?? ""}</td>
                                                <td>{fmtDateText(r.data_fattura)}</td>
                                                <td>{fmtDateText(r.data_competenza)}</td>
                                                <td>{fmtDateText(r.competence_start_date)}</td>
                                                <td>{fmtDateText(r.competence_end_date)}</td>
                                                <td>{r.importo_leggibile != null ? fmtEuro(r.importo_leggibile) : ""}</td>
                                                <td>{r.des_sede ?? ""}</td>
                                                <td>{r.id_settore ?? ""}</td>
                                                <td>{r.id_commessa ?? ""}</td>
                                                <td>{r.targa_veicolo ?? ""}</td>
                                                <td>{r.des_plan5 ?? ""}</td>
                                                <td>{r.des_plan6 ?? ""}</td>
                                                <td>{r.des_plan7 ?? ""}</td>
                                                <td>{r.des_plan10 ?? ""}</td>
                                                <td>{r.des_plan11 ?? ""}</td>
                                            </tr>
                                        ))}
                                        <tr className="admin-ricavi-row-totale">
                                            <td colSpan={10} className="admin-clienti-td-totale-label">Totale ({total} righe)</td>
                                            <td>{fmtEuro(totaleImporto)}</td>
                                            <td colSpan={9}></td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && !error && total > 0 && (
                    <div className="pagination-compact">
                        <button
                            className="pagination-btn-compact"
                            disabled={safePage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                            Prec
                        </button>
                        <div className="pagination-info-compact">
                            <span className="pagination-text-compact">
                                Pag <strong>{safePage}</strong> di <strong>{totalPages}</strong>
                            </span>
                            <span className="pagination-total-compact">({total} risultati totali)</span>
                        </div>
                        <button
                            className="pagination-btn-compact"
                            disabled={safePage === totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        >
                            Succ
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                        <div className="items-per-page-compact">
                            <span className="items-label-compact">Elementi per pagina:</span>
                            <div className="items-buttons-compact">
                                {PAGE_SIZE_OPTIONS.map((num) => (
                                    <button
                                        key={num}
                                        className={`items-btn-compact ${pageSize === num ? "active" : ""}`}
                                        onClick={() => {
                                            setPageSize(num);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataQuality;
